import { useState } from 'react';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import ContactModal from '../../../components/feature/ContactModal';
import SEO from '../../../components/feature/SEO';

export default function Accountants() {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="QBOVerifier Accountant Partner Program – Earn Commissions Protecting Clients"
        description="Join the QBOVerifier Accountant Partner Program. Earn 20% first-year commission and 10% ongoing. Protect your QuickBooks clients from Business Invoice Fraud and Business Email Fraud."
        keywords="QuickBooks accountant partner program, invoice fraud protection for accountants, QBOVerifier affiliate, bookkeeper commission program, QuickBooks security partner"
        canonical="/affiliates/accountants"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'QBOVerifier Accountant Partner Program',
          description: 'Accountant partner program offering commissions for referring QuickBooks invoice fraud protection services.',
          url: `${import.meta.env.VITE_SITE_URL || ''}/affiliates/accountants`,
          publisher: {
            '@type': 'Organization',
            name: 'QBOVerifier',
          },
        }}
      />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-green-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                Accountant Partner Program
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Help your clients protect their QuickBooks data with QBOVerifier. Join our exclusive accountant program and earn commissions while providing essential security services.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  onClick={openModal}
                  className="bg-green-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-green-700 transition-colors shadow-lg whitespace-nowrap"
                >
                  <i className="ri-user-add-line mr-2"></i>
                  Apply Now
                </button>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://storage.helloreaddy.io/project_files/76e476ea-c87d-45fb-a794-b173053f1134/1de24570-d7c3-4a66-b90d-917093a7b704_compressed_QBOVerified-Accountant.webp"
                alt="Professional Accountant"
                className="rounded-xl shadow-2xl object-cover w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Partner With QBOVerifier?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Expand your service offerings and generate additional revenue while helping your clients maintain data integrity.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-green-50 p-8 rounded-xl">
              <div className="w-16 h-16 bg-green-600 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-money-dollar-circle-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Recurring Revenue</h3>
              <p className="text-gray-600">
                Earn ongoing commissions from client subscriptions. Build a sustainable income stream that grows with your client base.
              </p>
            </div>
            
            <div className="bg-blue-50 p-8 rounded-xl">
              <div className="w-16 h-16 bg-blue-600 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Client Protection</h3>
              <p className="text-gray-600">
                Offer your clients essential data verification services that protect their financial records and ensure compliance.
              </p>
            </div>
            
            <div className="bg-purple-50 p-8 rounded-xl">
              <div className="w-16 h-16 bg-purple-600 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-customer-service-2-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Full Support</h3>
              <p className="text-gray-600">
                Get comprehensive training, marketing materials, and ongoing support to help you succeed with your clients.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">What You'll Offer Your Clients</h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-shield-check-line text-white text-sm"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Essential Fraud Protection</h3>
                    <p className="text-gray-600">An essential security solution that protects businesses from Business Email Fraud and Business Invoice Fraud — while actively building customer trust.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-palette-line text-white text-sm"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Brand-Matched Stylish Invoices</h3>
                    <p className="text-gray-600">Access to professionally designed invoices that align with each client's brand identity — enhancing market awareness and keeping them a step ahead of competitors.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-lightbulb-line text-white text-sm"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Demonstrate Proactive Expertise</h3>
                    <p className="text-gray-600">Position yourself as a forward-thinking advisor by introducing clients to innovative solutions that solve real business problems and demonstrate proactive risk management expertise.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-heart-line text-white text-sm"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Deeper, Longer-Lasting Relationships</h3>
                    <p className="text-gray-600">Build stronger client bonds by introducing solutions that protect what matters most — their reputation and the trust their customers place in them.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://static.readdy.ai/image/60cfa5760427af1c3d73f4665d1a36c9/ede5c96119dfbf9f53ad66e71fa1a544.jpeg"
                alt="Fraud Protection and Client Trust"
                className="rounded-xl shadow-2xl object-cover w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Commission Structure */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Commission Structure</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Transparent and competitive commission rates that reward your success.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-green-50 to-green-100 p-8 rounded-xl border border-green-200">
              <div className="text-center">
                <div className="text-4xl font-bold text-green-600 mb-2">20%</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">First Year Commission</h3>
                <p className="text-gray-600">
                  Earn 20% commission on all Annual client subscriptions during their first year with QBOVerifier.
                </p>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-8 rounded-xl border border-blue-200">
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">10%</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Ongoing Commission</h3>
                <p className="text-gray-600">
                  Continue earning 10% commission on renewals for as long as your clients remain active.
                </p>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-8 rounded-xl border border-purple-200">
              <div className="text-center">
                <div className="text-4xl font-bold text-purple-600 mb-2">$200</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Bonus Threshold</h3>
                <p className="text-gray-600">
                  Earn a $200 bonus when you refer 10 or more Annual subscriber clients in a calendar year.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-green-600 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">Ready to Protect Your Clients?</h2>
              <p className="text-xl mb-8">
                Join our accountant program today and start offering your clients the invoice security they need.
              </p>
              <button 
                onClick={openModal}
                className="bg-white text-green-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors shadow-lg whitespace-nowrap"
              >
                <i className="ri-mail-line mr-2"></i>
                Get Started Today
              </button>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-2xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Quick Facts</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                  <span className="text-gray-700">No upfront costs or fees</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                  <span className="text-gray-700">Comprehensive tutorial support provided</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                  <span className="text-gray-700">Partner support</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                  <span className="text-gray-700">Monthly commission payments</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      
      <ContactModal
        isOpen={isModalOpen}
        onClose={closeModal}
        title="Join Our Accountant Program"
        subtitle="Help your clients protect their QuickBooks data while earning recurring commissions"
      />
    </div>
  );
}
